import BusPackage from '../bus/BusPackage';
import FlightPackage from '../flight/FlightPackage';
import TrainPackage from '../train/TrainPackage';
import type TripPackage from '../TripPackage';
import { type PassengerCount } from '@itinerary/types';
import {
    type CabinClassRequest,
    type FlightPackageDto,
} from '@clients/FlightApi';
import { ServiceType, type Trip as TripDto } from '@clients/MultiRouteApi';
import { type GetServicesResultBus } from '@clients/BusApi';
import { type AvailableTrain } from '@clients/TrainApi';
import { SellTypeCode } from '@utils/train';

export class RouteTripFactory {
    private busDataConvertor(trip: TripDto): GetServicesResultBus {
        return {
            id: trip.serviceId,
            from: Number(trip.originCode),
            to: Number(trip.destinationCode),
            fromName: trip.originTitle,
            toName: trip.destinationTitle,
            corporation: trip.corporationTitle,
            departureTime: trip?.departureTime,
            arrivalTime: trip?.arrivalTime,
            price: trip.adultPrice,
            serviceNumber: trip.serviceNumber,
            capacity: trip.capacity,
            busType: trip.vehicleType,
            superCorporationID: trip.carrierCode
                ? Number(trip.carrierCode)
                : undefined,
            superCorporation: trip.carrierTitle,
            fromTerminal: trip.originStation,
            toTerminal: trip.destinationStation,
            fromCity: trip.originTitle,
            toCity: trip.destinationTitle,
            distance: undefined,
            providerID: trip.providerId ?? undefined,
            penaltyRates: null,
            busTypeID: trip.vehicleTypeId ?? undefined,
            discount: trip.discount ?? undefined,
            isVIP: !!trip.isVIP,
            points: undefined,
            precisionPoints: undefined,
            additionalInfo: undefined,
            finalDestinationCity: trip.destinationTitle,
            freeCancellationMins: null,
            weekday: null,
            // dateString: trip.departureDate,
            fromTerminalSpecified: undefined,
            toTerminalSpecified: undefined,
            fromTerminalName: trip.originStation,
            arrivalDateString: trip.arrivalTime,
            corportaionID: trip.corporationId ?? undefined,
            canCancelAfterClose: undefined,
            corporationFailed: undefined,
            corporationFailedUntil: null,
            preferredProvider: null,
            description: trip.description,
            reservable: true,
            autoReservable: false,
            isCar: false,
            shortTitle: trip.shortTitle,
            features: trip.features,
            // 'busTypeClass': trip.serviceClass ?? undefined,
            needsNationalCode: undefined,
            needsSinglePerson: undefined,
            needsSelectSeat: trip.needsSeatSelection,
            serviceNo: trip.serviceNumber,
            finalCityCode: trip.destinationCode,
            finalCityName: trip.destinationTitle,
            providerName: trip.providerName,
            isExclusive: false,
        };
    }

    private trainDataConvertor(trip: TripDto): AvailableTrain {
        return {
            id: trip.serviceId,
            from: Number(trip.originCode),
            to: Number(trip.destinationCode),
            fromName: trip.originTitle,
            toName: trip.destinationTitle,
            trainNumber: Number(trip.serviceNumber),
            departureTime: trip.departureTime,
            arrivalTime: trip.arrivalTime,
            provider: Number(trip.providerId),
            providerName: trip.providerName,
            corporationID: trip.corporationId ?? undefined,
            corporationName: trip.corporationTitle,
            // dateString: trip.departureDate,
            prices: [
                {
                    sellType: SellTypeCode.Family,
                    classes: [
                        {
                            owner: trip.carrierCode
                                ? Number(trip.carrierCode)
                                : 0,
                            ownerName: trip.carrierTitle,
                            id: trip.serviceId,
                            price: trip.adultPrice,
                            capacity: trip.capacity,
                            wagonName: trip.wagonName,
                            shortTitle: trip.shortTitle,
                            compartmentType: trip.compartmentType,
                            class: trip.serviceClass,
                            features: trip.features,
                            airConditioning: !!trip.airConditioning,
                            media: !!trip.media,
                            isCompartment: !!trip.isCompartment,
                            compartmentCapacity:
                                trip.compartmentCapacity ?? undefined,
                            discount: trip.discount ?? undefined,
                            wagonID: Number(trip.wagonId),
                            capacityString: trip.capacityString,
                            isAvailable: true,
                            cancellationTerms: trip.cancellationTerms,
                            hasCompartmentCapacity:
                                (trip.compartmentCapacity || 0) > 0,
                            roundTrip: false,
                        },
                    ],
                },
            ],
        };
    }

    private flightDataConvertor(trip: TripDto): FlightPackageDto {
        const durationInMillis =
            new Date(trip.arrivalTime ?? '').getTime() -
            new Date(trip.departureTime ?? '').getTime();
        return {
            Id: trip.serviceId?.toString() ?? '0',
            Prices: [
                {
                    UniqueId: trip.tripId?.toString(),
                    Id: trip.serviceId?.toString() ?? '0',
                    ProposalId: trip.reserveId,
                    Capacity: trip.capacity,
                    // 'DiscountToken'?: trip.
                    BookingClass: trip.bookingClass,
                    PassengerFares: [
                        {
                            Discount: trip.discount ?? undefined,
                            TotalFare: trip.adultPrice,
                            PaxType: 'ADL',
                        },
                        {
                            Discount: trip.discount ?? undefined,
                            TotalFare: trip.childPrice ?? 0,
                            PaxType: 'CHD',
                        },
                        {
                            Discount: trip.discount ?? undefined,
                            TotalFare: trip.infantPrice ?? 0,
                            PaxType: 'INF',
                        },
                    ],
                    // 'ProviderBaggageType': string | null;
                    CabinClass: trip.serviceClass as CabinClassRequest,
                    CabinClassDisplayName: trip.serviceClass,
                    FareRulesUrl: trip.cancellationTermsUrl,
                    ProviderName: trip.carrierTitle,
                    // 'CancellationTernEntities'?: Array<CancellationTernEntity> | null;
                    // 'FlightSpecialOffers'?: Array<FlightSpecialOfferDto> | null;
                    IsCharter: !!trip.isCharter,
                    // 'IgnorePriceLimit': trip.violatesPriceLimitations,
                    // 'ExtraTerms'?: trip.cancellationTerms,
                    // 'IncludeLowerCapacity': trip.cpa;
                    // 'IncludeHigherCapacity'?: boolean;
                    // 'NeedGetPrice'?: trip.need;
                    // 'IsAgencyHasPriorityOnShowingFlight'?: boolean;
                    // 'HasOfficialRatePrice'?: boolean;
                },
            ],
            Segments: [
                {
                    ConnectionTime: undefined,
                    TotalTime: null,
                    Legs: [
                        {
                            Id: trip.serviceId,
                            FlightNumber: trip.serviceNumber,
                            Origin: trip.originTitle,
                            OriginAirport: trip.originStation,
                            OriginCode: trip.originCode,
                            Destination: trip.destinationTitle,
                            DestinationAirport: trip.destinationStation,
                            JourneyTime: `${secondsToDurationTime(
                                durationInMillis / 1000
                            )}:00`,
                            DestinationCode: trip.destinationCode,
                            AirCraft: {
                                Id: trip.wagonId,
                                PersianTitle: trip.shortTitle,
                                ShortTitle: trip.shortTitle,
                            },
                            AirlineCode: trip.carrierCode,
                            OperatingAirlineCode: trip.operatingCarrierCode,
                            Airline: {
                                IataCode: trip.carrierCode,
                                PersianTitle: trip.carrierTitle,
                                Title: trip.carrierTitle,
                            },
                            OperatingAirline: {
                                IataCode: trip.operatingCarrierCode,
                                PersianTitle: trip.operatingCarrierTitle,
                                Title: trip.operatingCarrierTitle,
                            },
                            DepartureDateString: trip.departureDate,
                            DepartureTerminal: trip.originStation,
                            ArrivalTerminal: trip.destinationStation,
                            DepartureTime: trip?.departureTime,
                            ArrivalTime: trip?.arrivalTime,
                            Stops: trip.stops ?? undefined,
                            AirplaneCode: trip.serviceNumber,
                            // 'SuggestedServices'?: Array<SuggestedServiceDto> | null;
                            // 'AirlineAuxiliaryServices'?: Array<AirlineAuxiliaryServiceDto> | null;
                            // 'TechnicalStops'?: Array<TechnicalStopDto> | null;
                        },
                    ],
                },
            ],
            FareFamilyComponents: [
                {
                    RateCategory: null,
                    // 'Name'?: string | null;
                    CarrierAirlineCode: trip.carrierCode,
                    Origin: trip.originTitle,
                    Destination: trip.destinationTitle,
                    Descriptions: [
                        {
                            // Indicator: trip.capacityString,
                            AirlineCode: trip.carrierCode,
                            Details: trip.destinationStationDetails,
                        },
                    ],
                },
            ],
            AutomaticReserve: false,
            MustAppearInAutoReserve: false,
            MustAppearInPriceTrack: false,
            MustAppearInNotifyOnDecreasingPrice: false,
            IsFromMultilegSource: trip.isMultiLeg,
        };
    }

    routeTripFactory(
        trip: TripDto,
        passengerCount: PassengerCount = {
            adultCount: 1,
            foreignerCount: 0,
            childCount: 0,
            infantCount: 0,
        }
    ): TripPackage {
        const totalCount =
            passengerCount.adultCount +
            passengerCount.childCount +
            passengerCount.infantCount +
            (passengerCount.foreignerCount || 0);

        switch (trip.serviceType) {
            case ServiceType.NUMBER_0:
                return new FlightPackage(
                    this.flightDataConvertor(trip),
                    passengerCount,
                    {}
                );
            case ServiceType.NUMBER_1:
                return new TrainPackage(
                    this.trainDataConvertor(trip),
                    totalCount,
                    SellTypeCode.Family,
                    false
                );
            case ServiceType.NUMBER_2:
                return new BusPackage(this.busDataConvertor(trip), totalCount);
            default:
                throw new Error('service not supported');
        }
    }
}
